import { useEffect, useState } from 'react';

import FeatherIcon from 'feather-icons-react';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button, Checkbox, SearchInput } from 'components/FormComponents';

export const BulkAssignSupportUsersModal = ({
  visible,
  mode = 'assign',
  supportUsers = [],
  targetDescriptor = { ids: [], useAll: false, filters: {} },
  targetLabel = 'checks',
  allowIncludeSubChecks = false,
  fetchSummary = async () => ({ total: 0, user_counts: {} }),
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [search, setSearch] = useState('');
  const [assignSubMode, setAssignSubMode] = useState('add');
  const [includeSubChecks, setIncludeSubChecks] = useState(false);
  const [summary, setSummary] = useState(null);

  const selectedCount = targetDescriptor.useAll ? null : (targetDescriptor.ids?.length ?? 0);

  useEffect(() => {
    if (!visible) return;

    setSelectedUserIds([]);
    setSearch('');
    setAssignSubMode('add');
    setIncludeSubChecks(false);
    setSummary(null);

    fetchSummary({
      ids: targetDescriptor.ids,
      use_all: targetDescriptor.useAll,
      filters: targetDescriptor.filters,
    })
      .unwrap()
      .then(setSummary)
      .catch(() =>
        setSummary({
          total: selectedCount ?? 0,
          eligible_count: selectedCount ?? 0,
          total_requested: selectedCount ?? 0,
          user_counts: {},
        }),
      );
  }, [visible, targetDescriptor.useAll, selectedCount]);

  const eligible = summary?.eligible_count ?? summary?.total ?? 0;
  const requested = summary?.total_requested ?? selectedCount ?? eligible;
  const skipped = Math.max(0, requested - eligible);
  const userCounts = summary?.user_counts ?? {};

  const toggleUser = userId => {
    setSelectedUserIds(curr =>
      curr.includes(userId) ? curr.filter(id => id !== userId) : [...curr, userId],
    );
  };

  const eligibleUsers =
    mode === 'unassign'
      ? supportUsers.filter(user => (userCounts[user.id] ?? 0) > 0)
      : supportUsers;
  const filteredUsers = eligibleUsers.filter(user =>
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(search.toLowerCase()),
  );

  const handleClose = () => {
    setSelectedUserIds([]);
    onClose();
  };

  const handleSubmit = () => {
    onSubmit(selectedUserIds, {
      replace: mode === 'assign' && assignSubMode === 'replace',
      includeSubChecks,
    });
  };

  const eligibilityNote =
    skipped > 0
      ? ` ${skipped} of ${requested} selected ${targetLabel} will be skipped because they are completed, canceled, or still with the candidate.`
      : '';

  const description =
    mode === 'unassign'
      ? `Selected users will be removed from ${eligible} ${targetLabel}.${eligibilityNote}`
      : assignSubMode === 'replace'
        ? `Selected users will replace all existing assignees on ${eligible} ${targetLabel}.${eligibilityNote}`
        : `Selected users will be added to ${eligible} ${targetLabel}. Existing assignees will remain.${eligibilityNote}`;

  const submitLabel =
    mode === 'unassign'
      ? `Unassign from ${eligible} ${targetLabel}`
      : assignSubMode === 'replace'
        ? `Replace on ${eligible} ${targetLabel}`
        : `Add to ${eligible} ${targetLabel}`;

  return (
    <AnimatedModal
      visible={visible}
      onClose={handleClose}
      title={mode === 'unassign' ? 'Unassign support users' : 'Assign support users'}>
      <div className="flex w-[420px] flex-col">
        {mode === 'assign' && (
          <div className="mb-4 flex gap-2">
            <Button
              small
              type={assignSubMode === 'add' ? 'primary' : 'secondary'}
              onClick={() => setAssignSubMode('add')}>
              Add to existing
            </Button>
            <Button
              small
              type={assignSubMode === 'replace' ? 'primary' : 'secondary'}
              onClick={() => setAssignSubMode('replace')}>
              Replace existing
            </Button>
          </div>
        )}

        <p className="mb-4 text-neutral-6">{description}</p>

        {allowIncludeSubChecks && (
          <Checkbox
            checked={includeSubChecks}
            onChange={() => setIncludeSubChecks(current => !current)}
            className="mb-4"
            label={
              <span className="flex flex-col">
                <span className="text-neutral-8">Also apply to sub-checks</span>
                <span className="text-sm text-neutral-6">
                  {mode === 'unassign'
                    ? 'Unassign these users from sub-checks under the selected checks too.'
                    : 'Sub-checks under the selected checks are assigned the same way, without affecting checks assigned separately.'}
                </span>
              </span>
            }
          />
        )}

        {selectedUserIds.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {selectedUserIds.map(userId => {
              const user = supportUsers.find(candidate => candidate.id === userId);
              if (!user) return null;
              return (
                <span
                  key={userId}
                  className="inline-flex items-center gap-1 rounded-2xl bg-primary-4/10 px-3 py-1 text-sm text-primary-5">
                  {user.first_name} {user.last_name}
                  <FeatherIcon
                    icon="x"
                    size={14}
                    className="cursor-pointer"
                    onClick={() => toggleUser(userId)}
                  />
                </span>
              );
            })}
          </div>
        )}

        <SearchInput
          placeholder="Search support users"
          value={search}
          onChange={setSearch}
          className="mb-4"
        />

        {mode === 'unassign' && summary && eligibleUsers.length === 0 && (
          <p className="text-neutral-6">
            None of the selected {targetLabel} have support users assigned.
          </p>
        )}

        <div className="flex max-h-[320px] flex-col gap-1 overflow-y-auto">
          {filteredUsers.map(user => {
            const count = userCounts[user.id] ?? 0;
            const subtext =
              count === 0
                ? 'Not currently assigned'
                : eligible > 0 && count === eligible
                  ? `Assigned to all ${eligible}`
                  : `Assigned to ${count} of ${eligible}`;

            return (
              <Checkbox
                key={user.id}
                checked={selectedUserIds.includes(user.id)}
                onChange={() => toggleUser(user.id)}
                label={
                  <span className="flex flex-col">
                    <span className="text-neutral-8">
                      {user.first_name} {user.last_name}
                    </span>
                    <span className="text-sm text-neutral-6">{subtext}</span>
                  </span>
                }
              />
            );
          })}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button type="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            disabled={selectedUserIds.length === 0 || eligible === 0}
            onClick={handleSubmit}>
            {submitLabel}
          </Button>
        </div>
      </div>
    </AnimatedModal>
  );
};
